import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { formatDate } from '../lib/dates';
import { api, ApiError, asList } from '../lib/api';

/* ============================================================================
   THE TEACHER'S CLASSROOM

   Two things:

     HOMEWORK — a task with a due date, and a LIVE COUNT of who has actually done
     it. That count is the whole reason this is worth building: a teacher sees who
     hasn't done the work without collecting forty notebooks.

     CONVERSATIONS — "Aarav has been unusually quiet this week. Can we talk?" The
     most valuable message a school sends, and there was previously no way to send
     it.

   ⚠ The oversight notice is shown to the teacher, ON PURPOSE. Hidden monitoring
   gets discovered and becomes a scandal; disclosed oversight produces professional
   messages — and protects the teacher if a parent ever disputes what was said.
   ========================================================================== */

interface TeacherClass { classId: string; label: string; studentCount: number; }
interface Subject { subjectId: string; name: string; }
interface Student { studentId: string; fullName: string; admissionNumber: string; }

interface Homework {
  id: string; academicClassId: string; classLabel: string;
  subjectId: string; subjectName: string; teacherName: string;
  title: string; details: string;
  assignedOn: string; dueOn: string; attachmentUrl: string | null;
  isWithdrawn: boolean; isOverdue: boolean;
  doneCount: number; totalStudents: number;
}
interface Completion {
  studentId: string; studentName: string; admissionNumber: string;
  done: boolean; markedAt: string | null; note: string | null; wasLate: boolean;
}
interface Message {
  id: string; author: string; authorName: string; body: string;
  sentAt: string; readAt: string | null;
  /** ⚠ A flag, not a URL — the bytes come from an endpoint that re-checks who is asking. */
  hasPhoto?: boolean;
}
interface Conversation {
  id: string; studentId: string; studentName: string;
  teacherUserId: string; teacherName: string;
  subject: string; isClosed: boolean;
  startedAt: string; lastMessageAt: string;
  unreadCount: number; messages: Message[];
}

type Tab = 'homework' | 'messages';

export function ClassroomPage() {
  const [tab, setTab] = useState<Tab>('homework');
  const [classes, setClasses] = useState<TeacherClass[]>([]);

  useEffect(() => {
    api.get<TeacherClass[]>('/api/classroom/my-classes').then((r) => setClasses(asList(r))).catch(() => setClasses([]));
  }, []);

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Teaching</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>My classroom</h1>
        </div>
      </div>

      {classes.length === 0 ? (
        <div className="empty">
          <h3>You're not on the timetable yet</h3>
          <p>
            Homework and messages are scoped to the classes you teach. Once the school puts you on the
            timetable, your classes appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="tab-strip" style={{ gap: 4, marginBottom: 18 }}>
            <TabBtn on={tab === 'homework'} onClick={() => setTab('homework')}>Homework</TabBtn>
            <TabBtn on={tab === 'messages'} onClick={() => setTab('messages')}>Messages to parents</TabBtn>
          </div>

          {tab === 'homework' && <HomeworkTab classes={classes} />}
          {tab === 'messages' && <MessagesTab classes={classes} />}
        </>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- homework */

function HomeworkTab({ classes }: { classes: TeacherClass[] }) {
  const [rows, setRows] = useState<Homework[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<Homework | null>(null);

  const load = useCallback(() => {
    api.get<Homework[]>('/api/classroom/homework').then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 14 }}>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>
          + Set homework
        </button>
      </div>

      {!rows ? (
        <div className="card" style={{ padding: 20 }}><span className="sk" style={{ width: '50%' }} /></div>
      ) : rows.length === 0 ? (
        <div className="empty">
          <h3>No homework set</h3>
          <p>Set a task and every family in the class is told — with the due date.</p>
          <button className="btn btn-accent" onClick={() => setAdding(true)}>+ Set homework</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((h) => {
            const pct = h.totalStudents ? Math.round((h.doneCount / h.totalStudents) * 100) : 0;
            return (
              <button key={h.id} onClick={() => setViewing(h)} className="card"
                style={{ padding: 16, textAlign: 'left', cursor: 'pointer', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 16, fontFamily: 'var(--font-display)' }}>{h.title}</strong>
                  <span className="pill pill-muted" style={{ fontSize: 11 }}>{h.subjectName}</span>
                  <span className="pill pill-muted" style={{ fontSize: 11 }}>{h.classLabel}</span>
                  <span style={{
                    marginLeft: 'auto', fontSize: 12.5,
                    color: h.isOverdue ? 'var(--brand)' : 'var(--ink-3)',
                    fontWeight: h.isOverdue ? 600 : 400,
                  }}>
                    due {new Date(h.dueOn).toLocaleDateString([], { day: 'numeric', month: 'short' })}
                  </span>
                </div>

                <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: '6px 0 10px' }}>
                  {h.details.length > 110 ? h.details.slice(0, 110) + '…' : h.details}
                </p>

                {/* ⚠ THE PAYOFF — who's actually done it. */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{
                    flex: 1, height: 6, borderRadius: 4,
                    background: 'var(--line)', overflow: 'hidden', maxWidth: 200,
                  }}>
                    <div style={{
                      height: '100%', width: `${pct}%`,
                      background: pct === 100 ? 'var(--ok-ink, #1e7d4f)' : 'var(--accent)',
                    }} />
                  </div>
                  <span style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                    <strong>{h.doneCount}</strong> of {h.totalStudents} done
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {adding && <SetHomeworkDialog classes={classes} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {viewing && <CompletionsDialog homework={viewing} onClose={() => setViewing(null)} onChanged={() => { setViewing(null); load(); }} />}
    </div>
  );
}

function SetHomeworkDialog({ classes, onClose, onDone }: {
  classes: TeacherClass[]; onClose: () => void; onDone: () => void;
}) {
  const [classId, setClassId] = useState(classes.length === 1 ? classes[0].classId : '');
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [f, setF] = useState({ subjectId: '', title: '', details: '', dueOn: '', attachmentUrl: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only the subjects this teacher teaches to THIS class — the form can't offer nonsense.
  useEffect(() => {
    if (!classId) { setSubjects([]); return; }
    api.get<Subject[]>(`/api/classroom/classes/${classId}/subjects`)
      .then((r) => { setSubjects(r); if (r.length === 1) setF((p) => ({ ...p, subjectId: r[0].subjectId })); })
      .catch(() => setSubjects([]));
  }, [classId]);

  const save = async () => {
    if (!classId || !f.subjectId || !f.title.trim() || !f.details.trim() || !f.dueOn) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/classroom/homework', {
        academicClassId: classId,
        subjectId: f.subjectId,
        title: f.title.trim(),
        details: f.details.trim(),
        dueOn: f.dueOn,
        attachmentUrl: f.attachmentUrl.trim() || null,
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>Set homework</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 16 }}>
          Every family in the class is notified, with the due date.
        </p>

        {error && <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {classes.length > 1 && (
            <div>
              <label className="label">Class</label>
              <select className="input" value={classId} onChange={(e) => { setClassId(e.target.value); setF({ ...f, subjectId: '' }); }}>
                <option value="">Select…</option>
                {classes.map((c) => (
                  <option key={c.classId} value={c.classId}>{c.label} ({c.studentCount} students)</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="label">Subject</label>
            <select className="input" value={f.subjectId} onChange={(e) => setF({ ...f, subjectId: e.target.value })}
              disabled={!classId}>
              <option value="">Select…</option>
              {subjects.map((s) => <option key={s.subjectId} value={s.subjectId}>{s.name}</option>)}
            </select>
          </div>

          <div>
            <label className="label">Title</label>
            <input className="input" placeholder="Chapter 4 exercises"
              value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </div>

          <div>
            <label className="label">The task</label>
            <textarea className="input" rows={4}
              placeholder="Complete questions 1–12 on page 87. Bring your graph book tomorrow."
              value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} />
          </div>

          <div className="form-row">
            <div>
              <label className="label">Due on</label>
              <input className="input" type="date" value={f.dueOn}
                onChange={(e) => setF({ ...f, dueOn: e.target.value })} />
            </div>
            <div>
              <label className="label">Link (optional)</label>
              <input className="input" placeholder="A worksheet, a video"
                value={f.attachmentUrl} onChange={(e) => setF({ ...f, attachmentUrl: e.target.value })} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent"
            disabled={busy || !classId || !f.subjectId || !f.title.trim() || !f.details.trim() || !f.dueOn}
            onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Setting…' : 'Set homework'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The diary photograph — take it, replace it, remove it.
 *
 * ⚠ `capture="environment"` is the whole feature on a phone: it opens the CAMERA rather than a file
 * browser. A teacher standing in front of the board taps once and shoots. On a laptop the same input
 * falls back to a file picker, so one control serves both without a second code path.
 *
 * The photo is fetched with the auth header and shown as a blob, not linked — the endpoint checks who
 * is asking, and an <img src="/api/..."> cannot carry a token.
 */
function DiaryPhotoPanel({ homework, onChanged }: { homework: Homework; onChanged: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const hasPhoto = !!homework.attachmentUrl;

  useEffect(() => {
    if (!hasPhoto) { setSrc(null); return; }

    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const blob = await api.blob(`/api/classroom/homework/${homework.id}/photo`);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setSrc(null);
      }
    })();

    // Revoked on unmount — without this a teacher opening several homeworks leaks one object URL
    // each until the tab is closed.
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [homework.id, hasPhoto]);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      await api.postForm(`/api/classroom/homework/${homework.id}/photo`, body);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not upload that photo.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/api/classroom/homework/${homework.id}/photo`);
      onChanged();
    } catch (e) {
      // The domain refuses to remove the photo when there is no typed task — that would leave the
      // homework empty and tell a parent nothing.
      setError(e instanceof ApiError ? e.message : 'Could not remove the photo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {src && (
        <a href={src} target="_blank" rel="noreferrer" style={{ display: 'block', marginBottom: 8 }}>
          <img
            src={src}
            alt="The homework diary"
            style={{
              width: '100%', maxHeight: 220, objectFit: 'cover', objectPosition: 'top',
              borderRadius: 10, border: '1px solid var(--line)',
            }}
          />
        </a>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void upload(file);
          e.target.value = '';   // so choosing the same file twice still fires
        }}
      />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? 'Uploading\u2026' : hasPhoto ? 'Replace the photo' : 'Photograph the diary'}
        </button>

        {hasPhoto && (
          <button className="btn" disabled={busy} onClick={() => void remove()}>
            Remove
          </button>
        )}
      </div>

      {!hasPhoto && !error && (
        <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '6px 0 0' }}>
          On a phone this opens the camera. Photograph the board instead of typing it out.
        </p>
      )}

      {error && (
        <p style={{ fontSize: 12, color: 'var(--danger, #b3261e)', margin: '6px 0 0' }}>{error}</p>
      )}
    </div>
  );
}

function CompletionsDialog({ homework: h, onClose, onChanged }: {
  homework: Homework; onClose: () => void; onChanged: () => void;
}) {
  const [rows, setRows] = useState<Completion[] | null>(null);

  useEffect(() => {
    api.get<Completion[]>(`/api/classroom/homework/${h.id}/completions`)
      .then(setRows).catch(() => setRows([]));
  }, [h.id]);

  const notDone = rows?.filter((r) => !r.done) ?? [];

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 2 }}>{h.title}</h2>
        <p style={{ color: 'var(--ink-3)', fontSize: 12.5, marginBottom: 16 }}>
          {h.subjectName} · {h.classLabel} · due {formatDate(h.dueOn)}
        </p>

        <DiaryPhotoPanel homework={h} onChanged={onChanged} />

        {/* Not-done first — those are the ones the teacher needs to see. */}
        {notDone.length > 0 && (
          <div className="pill pill-warn" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 14, justifyContent: 'flex-start' }}>
            <strong>{notDone.length} {notDone.length === 1 ? 'student hasn\u2019t' : 'students haven\u2019t'} done it.</strong>
          </div>
        )}

        {!rows ? (
          <span className="sk" style={{ width: '60%' }} />
        ) : (
          <div style={{ maxHeight: '46vh', overflowY: 'auto', marginBottom: 14 }}>
            {rows.map((r) => (
              <div key={r.studentId} style={{
                display: 'flex', gap: 10, alignItems: 'center',
                padding: '9px 0', borderBottom: '1px solid var(--line-2)',
              }}>
                <span style={{ fontSize: 15, width: 18 }}>{r.done ? '✓' : '○'}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: r.done ? 'var(--ink)' : 'var(--ink-2)' }}>
                    {r.studentName}
                  </div>
                  {r.note && (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>“{r.note}”</div>
                  )}
                </div>
                {r.done && r.wasLate && (
                  <span className="pill pill-warn" style={{ fontSize: 10, marginLeft: 'auto' }}>late</span>
                )}
              </div>
            ))}
          </div>
        )}

        <button className="btn" onClick={async () => {
          if (!window.confirm('Withdraw this homework? Families will see it as cancelled.')) return;
          await api.post(`/api/classroom/homework/${h.id}/withdraw`, {}).catch(() => {});
          onChanged();
        }}>
          Withdraw homework
        </button>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- messages */

function MessagesTab({ classes }: { classes: TeacherClass[] }) {
  const [rows, setRows] = useState<Conversation[] | null>(null);
  const [open, setOpen] = useState<Conversation | null>(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => {
    api.get<Conversation[]>('/api/classroom/conversations').then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  return (
    <div>
      {/* ⚠ THE DISCLOSURE. Shown to the teacher, on purpose. */}
      <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
        <strong style={{ fontSize: 13.5 }}>These conversations are part of the school record.</strong>
        <p style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 3 }}>
          School administrators can review them. This protects you as much as anyone —
          if a parent ever disputes what was said, the record is right here.
        </p>
      </div>

      <div style={{ display: 'flex', marginBottom: 14 }}>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setStarting(true)}>
          + Write to a parent
        </button>
      </div>

      {!rows ? (
        <div className="card" style={{ padding: 20 }}><span className="sk" style={{ width: '50%' }} /></div>
      ) : rows.length === 0 ? (
        <div className="empty">
          <h3>No conversations</h3>
          <p>Write to a parent about one child — a concern, or something they did well.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((c) => (
            <button key={c.id} onClick={async () => {
              const full = await api.get<Conversation>(`/api/classroom/conversations/${c.id}`);
              setOpen(full); load();
            }} className="card" style={{
              padding: 14, textAlign: 'left', cursor: 'pointer', width: '100%',
              background: c.unreadCount > 0 ? 'var(--surface-2)' : undefined,
            }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                {c.unreadCount > 0 && (
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand)' }} />
                )}
                <strong style={{ fontSize: 15 }}>{c.studentName}</strong>
                <span style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>— {c.subject}</span>
                {c.isClosed && <span className="pill pill-muted" style={{ fontSize: 10 }}>closed</span>}
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                  {formatDate(c.lastMessageAt)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {starting && <StartDialog classes={classes} onClose={() => setStarting(false)} onDone={() => { setStarting(false); load(); }} />}
      {open && <ThreadDialog conversation={open} isTeacher onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function StartDialog({ classes, onClose, onDone }: {
  classes: TeacherClass[]; onClose: () => void; onDone: () => void;
}) {
  const [classId, setClassId] = useState(classes.length === 1 ? classes[0].classId : '');
  const [students, setStudents] = useState<Student[]>([]);
  const [f, setF] = useState({ studentId: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!classId) { setStudents([]); return; }
    api.get<Student[]>(`/api/classroom/classes/${classId}/students`)
      .then((r) => setStudents(asList(r))).catch(() => setStudents([]));
  }, [classId]);

  const save = async () => {
    if (!f.studentId || !f.subject.trim() || !f.message.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/classroom/conversations', {
        studentId: f.studentId, subject: f.subject.trim(), message: f.message.trim(),
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not send.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 14 }}>Write to a parent</h2>

        {error && <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {classes.length > 1 && (
            <div>
              <label className="label">Class</label>
              <select className="input" value={classId} onChange={(e) => { setClassId(e.target.value); setF({ ...f, studentId: '' }); }}>
                <option value="">Select…</option>
                {classes.map((c) => <option key={c.classId} value={c.classId}>{c.label}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">About which child?</label>
            <select className="input" value={f.studentId} onChange={(e) => setF({ ...f, studentId: e.target.value })}
              disabled={!classId}>
              <option value="">Select…</option>
              {students.map((s) => <option key={s.studentId} value={s.studentId}>{s.fullName}</option>)}
            </select>
          </div>

          <div>
            <label className="label">What is this about?</label>
            <input className="input" placeholder="Progress in maths / a concern / something they did well"
              value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
          </div>

          <div>
            <label className="label">Message</label>
            <textarea className="input" rows={5}
              placeholder="Aarav has been unusually quiet this week. Could we have a short chat?"
              value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !f.studentId || !f.subject.trim() || !f.message.trim()}
            onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/** The thread. Shared by teacher, family, and the school's oversight view. */
/**
 * A photograph inside a message.
 *
 * Fetched rather than linked, for the same reason as the homework diary photo: the endpoint wants
 * the caller's bearer token and an <img src="/api/…"> cannot send one — the browser issues that
 * request by itself, without headers. So the bytes are fetched here and turned into a blob URL.
 *
 * ⚠ The object URL is revoked on unmount. Without it, a parent scrolling a term of messages leaks
 * one per photo until the tab is closed, and these are phone photographs — megabytes each.
 */
function MessagePhoto({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const blob = await api.blob(url);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  // ⚠ Says so rather than showing a broken image icon. A photo that will not load is a thing the
  // person needs told, because the message may have been nothing but the photo.
  if (failed) {
    return (
      <div style={{ fontSize: 12.5, opacity: .8, padding: '6px 2px' }}>
        This photo could not be loaded.
      </div>
    );
  }

  if (!src) {
    return <div className="sk" style={{ width: 200, height: 140, borderRadius: 8 }} />;
  }

  return (
    <img
      src={src}
      alt="Attached photo"
      // Opens full size in a new tab — a photograph of a maths problem is unreadable at thumbnail
      // size, and pinch-to-zoom on an inline image inside a scrolling dialog fights the scroll.
      onClick={() => window.open(src, '_blank', 'noopener')}
      style={{
        display: 'block', maxWidth: '100%', maxHeight: 260, width: 'auto',
        borderRadius: 8, cursor: 'zoom-in',
      }}
    />
  );
}

export function ThreadDialog({ conversation, isTeacher, readOnly, onClose }: {
  conversation: Conversation; isTeacher?: boolean; readOnly?: boolean; onClose: () => void;
}) {
  const [c, setC] = useState(conversation);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const base = isTeacher ? '/api/classroom' : '/api/portal/classroom';

  // ⚠ An administrator reading a thread for oversight is neither the teacher nor the family, and
  // their photos come from the oversight route. Sending them down the portal route would 403 —
  // the words would render and every picture would be a broken box, which is the failure that
  // makes oversight look like it works when it does not.
  const photoUrl = (messageId: string) => readOnly
    ? `/api/admin/conversations/${c.id}/messages/${messageId}/photo`
    : `${base}/conversations/${c.id}/messages/${messageId}/photo`;

  const sendPhoto = async (file: File) => {
    setBusy(true);
    setPhotoError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      // The typed line rides along with the picture, so "here is the sum" is one message and not two.
      if (body.trim()) form.append('body', body.trim());
      setC(await api.postForm<Conversation>(`${base}/conversations/${c.id}/photo`, form));
      setBody('');
    } catch (e) {
      setPhotoError(e instanceof ApiError ? e.message : 'The photo could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const updated = await api.post<Conversation>(`${base}/conversations/${c.id}/reply`, { body: body.trim() });
      setC(updated);
      setBody('');
    } catch { /* surfaced on reload */ }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, display: 'flex', flexDirection: 'column' }}>
        <div style={{ marginBottom: 12 }}>
          <h2 className="h-display" style={{ fontSize: 18 }}>{c.subject}</h2>
          <p style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
            About <strong>{c.studentName}</strong> · with {isTeacher ? 'the family' : c.teacherName}
          </p>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', maxHeight: '48vh', marginBottom: 12 }}>
          {c.messages.map((m) => {
            const mine = isTeacher ? m.author === 'Teacher' : m.author === 'Family';
            return (
              <div key={m.id} style={{
                display: 'flex', flexDirection: 'column',
                alignItems: mine ? 'flex-end' : 'flex-start',
                marginBottom: 10,
              }}>
                <div style={{
                  maxWidth: '82%', padding: m.hasPhoto && !m.body ? 6 : '10px 13px', borderRadius: 12,
                  background: mine ? 'var(--brand)' : 'var(--surface-2)',
                  color: mine ? '#fff' : 'var(--ink)',
                  fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                }}>
                  {/* ⚠ The photo first, then the words. A parent sends the picture and adds a line
                      about it; reading the caption before seeing what it captions is backwards. */}
                  {m.hasPhoto && <MessagePhoto url={photoUrl(m.id)} />}
                  {m.body && <div style={{ marginTop: m.hasPhoto ? 8 : 0 }}>{m.body}</div>}
                </div>
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 3 }}>
                  {m.authorName} · {new Date(m.sentAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {mine && m.readAt && ' · read'}
                </span>
              </div>
            );
          })}
        </div>

        {readOnly ? (
          <p style={{ fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'center' }}>
            You're viewing this as a school administrator. You cannot reply.
          </p>
        ) : c.isClosed ? (
          <p style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center' }}>
            This conversation is closed.
          </p>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* ⚠ A LABEL WRAPPING A HIDDEN INPUT, not a button that clicks a ref. It is the one
                  form that a phone reliably offers "Take photo" for, and on iOS a programmatic
                  click on a file input is ignored outside a direct user gesture. */}
              <label className="btn" style={{ flex: '0 0 auto', cursor: busy ? 'default' : 'pointer' }}
                title="Attach a photo">
                📎
                <input type="file" accept="image/*" capture="environment" disabled={busy}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    // ⚠ Cleared after reading. Without this, choosing the same file twice in a row
                    // fires no change event and the second send silently does nothing.
                    e.target.value = '';
                    if (f) void sendPhoto(f);
                  }} />
              </label>
              <input className="input" value={body} placeholder="Write a reply…"
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
                style={{ flex: 1 }} />
              <button className="btn btn-accent" disabled={busy || !body.trim()} onClick={send}>
                {busy ? '…' : 'Send'}
              </button>
            </div>
            {photoError && (
              <p style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{photoError}</p>
            )}
          </div>
        )}

        {isTeacher && !c.isClosed && (
          <button className="btn" style={{ marginTop: 10 }} onClick={async () => {
            await api.post(`/api/classroom/conversations/${c.id}/close`, {}).catch(() => {});
            onClose();
          }}>
            Close this conversation
          </button>
        )}
      </div>
    </div>
  );
}

function TabBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14,
      color: on ? 'var(--ink)' : 'var(--ink-3)', fontWeight: on ? 600 : 400,
      borderBottom: on ? '2px solid var(--brand)' : '2px solid transparent',
    }}>{children}</button>
  );
}
